import Modal from "@/components/controls/Modal";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import Form, {
  FormField,
  SingleSelectOptions
} from "@/components/controls/Form/Form";
import {
  DisqualifiedReasons,
  disqualifyModalMode,
  LeadData
} from "@/pages/leads/types";
import {
  useClosedAsQualifiedReasons,
  useDisqualifiedCompanyReasons,
  useDisqualifiedReasons
} from "@/pages/leads/hooks";
import {
  closedAsQualifiedReasonsFromIdx,
  disqualifiedReasonsFromIdx
} from "@/pages/leads/helpers";
import { CheckboxGroupItem } from "@/components/ui/checkbox-group";
import { RadioOption } from "@/components/ui/radio-group";
import { forEach, map } from "lodash";

export interface DisqualifyLeadModalState {
  itemToDisqualify?: LeadData | null;
  isOpen: boolean;
  mode: disqualifyModalMode;
  selectedReason: number | null;
  notesInput: string | null;
  selectedCompanyReason: number | null;
}

interface Props extends DisqualifyLeadModalState {
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  disqualifyLead: (data?: {
    reason: number;
    notes: string;
    disqualifyCompany: boolean;
    disqualifyPerson: boolean;
    disqualifiedCompanyReason: number;
  }) => void;
}

const DisqualifyLeadModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  disqualifyLead,
  mode,
  selectedReason,
  notesInput,
  selectedCompanyReason
}) => {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);

  const isEditMode = Boolean(selectedReason);

  const reasons =
    (mode === "disqualified"
      ? useDisqualifiedReasons().data?.disqualifiedReasons
      : useClosedAsQualifiedReasons().data?.closedAsQualifiedReasons) ?? [];
  const disqualifiedCompanyReasons =
    useDisqualifiedCompanyReasons().data?.disqualifiedCompanyReasons;

  const [isFormValid, setIsFormValid] = useState(false);
  const [selectedReasonState, setSelectedReasonState] =
    useState(selectedReason);
  const [selectedCompanyReasonState, setSelectedCompanyReasonState] = useState(
    selectedCompanyReason
  );
  const [isEntityChecked, setIsEntityChecked] = useState(false);

  const getReasonOptions = () => {
    const reasonOptions: SingleSelectOptions[] = [];
    forEach(reasons, (reasonValue) => {
      reasonOptions.push({
        id: reasonValue.id,
        displayName:
          mode === "disqualified"
            ? disqualifiedReasonsFromIdx(t, reasons, reasonValue.id)
            : closedAsQualifiedReasonsFromIdx(t, reasons, reasonValue.id)
      });
    });
    return reasonOptions;
  };

  const reasonField: FormField = {
    name: "reason",
    label: t(`leads.modal.${mode}.reason`),
    type: "singleSelect",
    required: true,
    placeholder: t("leads.modal.reasonPlaceholder"),
    options: getReasonOptions(),
    defaultValue: selectedReason?.toString(),
    dependencies: ["notes"],
    onChange: (value) => {
      setSelectedReasonState(Number(value));
    }
  };

  const notesField: FormField = {
    name: "notes",
    label: t("leads.modal.notes"),
    type: "textarea",
    defaultValue: notesInput?.toString(),
    followingComponent:
      !isEditMode &&
      mode === "disqualified" &&
      (selectedReasonState === DisqualifiedReasons.UNQUALIFIED_COMPANY ||
        selectedReasonState === DisqualifiedReasons.UNQUALIFIED_PERSON) ? (
        <div className="flex items-center">
          <CheckboxGroupItem
            id="my-checkbox"
            label=""
            checked={isEntityChecked}
            onCheckedChange={setIsEntityChecked}
          />
          <Text variant="bodySecondary">
            {t("leads.modal.requestToDisqualify", {
              entity:
                selectedReasonState === DisqualifiedReasons.UNQUALIFIED_COMPANY
                  ? t("leads.modal.company")
                  : t("leads.modal.person")
            })}
          </Text>
        </div>
      ) : undefined
  };

  const companyReasonsOptions: RadioOption[] = map(
    disqualifiedCompanyReasons,
    (reason) => ({ value: reason.id.toString(), label: reason.name })
  );

  const getFormFields = () => {
    const companyReasonsField: FormField = {
      name: "disqualifiedCompanyReason",
      label: t("leads.modal.disqualifiedCompanyReason"),
      type: "radio",
      required: true,
      defaultValue: selectedCompanyReason?.toString(),
      options: companyReasonsOptions,
      onChange: (value) => {
        setSelectedCompanyReasonState(Number(value));
      }
    };
    const showCompanyReasonsComponent =
      mode === "disqualified" &&
      selectedReasonState === DisqualifiedReasons.UNQUALIFIED_COMPANY;

    return showCompanyReasonsComponent
      ? [reasonField, companyReasonsField, notesField]
      : [reasonField, notesField];
  };

  useEffect(() => {
    if (isOpen) {
      setSelectedReasonState(selectedReason);
      setSelectedCompanyReasonState(selectedCompanyReason);
      setIsEntityChecked(false);
      setIsFormValid(false);
    } else {
      setSelectedReasonState(null);
      setSelectedCompanyReasonState(null);
      setIsEntityChecked(false);
      setIsFormValid(false);
      formRef.current?.reset();
    }
  }, [isOpen, selectedReason, selectedCompanyReason]);

  const handleSubmit = (data: {
    reason: string;
    notes: string;
    disqualifiedCompanyReason: string;
  }) => {
    disqualifyLead({
      ...data,
      reason: Number(selectedReasonState),
      disqualifyPerson:
        isEntityChecked &&
        selectedReasonState === DisqualifiedReasons.UNQUALIFIED_PERSON,
      disqualifyCompany:
        isEntityChecked &&
        selectedReasonState === DisqualifiedReasons.UNQUALIFIED_COMPANY,
      disqualifiedCompanyReason: Number(selectedCompanyReasonState)
    });
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  const handleDisqualifyLead = () => {
    formRef.current?.requestSubmit();
  };

  return (
    <Modal
      open={isOpen}
      setIsOpen={setIsOpen}
      contentClassname="w-[35rem]"
      data-test="leads-disqualify-modal"
      title={
        <div className="flex flex-col gap-2">
          <Text
            as="div"
            variant="lgSubtitleH1"
            data-test="leads-disqualify-modal-title"
          >
            {t(`leads.modal.${mode}.title`)}
          </Text>
        </div>
      }
      content={
        <div
          className="flex flex-col gap-1"
          data-test="leads-disqualify-modal-content"
        >
          {/* TODO: uncomment when there is a qualified company definition in settings */}
          {/* <div className="inline-flex flex-wrap gap-1 pl-4 pt-2">
            <Text variant="bodySecondary">{t("leads.modal.subtitle")}</Text>
            <ClickableTooltip
              trigger={<Text variant="smLink">{t("leads.modal.company")}</Text>}
              content="company tooltip"
            />
            <Text variant="bodySecondary">{t("leads.modal.and")}</Text>
            <div className="flex gap-[1px]">
              <ClickableTooltip
                trigger={
                  <Text variant="smLink">{t("leads.modal.person")}</Text>
                }
                content={t("qualifications.person")}
              />
              <Text variant="bodySecondary">?</Text>
            </div>
          </div> */}
          <div className="flex flex-col gap-3 w-[35rem] p-4 overflow-y-auto">
            <Form
              fields={getFormFields()}
              isFormValid={(isValid) => setIsFormValid(isValid)}
              onSubmit={handleSubmit}
              formRef={formRef}
            />
          </div>
        </div>
      }
      footer={
        <div
          className="flex justify-end gap-2"
          data-test="leads-disqualify-modal-footer"
        >
          <Button
            data-test="leads-disqualify-modal-cancel-button"
            variant="secondary"
            onClick={handleCancel}
          >
            {t("cancel")}
          </Button>
          <Button
            data-test="leads-disqualify-modal-submit-button"
            destructive
            disabled={!isFormValid}
            onClick={handleDisqualifyLead}
          >
            {t(`leads.modal.${mode}.title`)}
          </Button>
        </div>
      }
    />
  );
};

export default DisqualifyLeadModal;
